import type { ModelComparisonReport } from '../types';

/**
 * Generate a markdown report from model comparison results
 * @param report - The comparison report data
 * @returns Formatted markdown string
 */
export function generateMarkdownReport(report: ModelComparisonReport): string {
  const { timestamp, promptTemplate, parameters, sectionTitle, results } =
    report;

  // Build markdown sections
  const sections: string[] = [];

  // Header
  sections.push(`# Model Comparison Report: ${sectionTitle}`);
  sections.push('');
  sections.push(`**Generated**: ${timestamp}`);
  sections.push(
    `**Parameters**: ${Object.keys(parameters).length} parameter(s)`
  );
  sections.push(`**Models Compared**: ${results.length}`);
  sections.push('');
  sections.push('---');
  sections.push('');

  // Filled Prompt Section
  sections.push('## Prompt');
  sections.push('');
  sections.push('```');
  sections.push(fillPromptTemplate(promptTemplate, parameters));
  sections.push('```');
  sections.push('');

  // Results Section - only include successful results
  const successfulResults = results.filter(r => r.success);

  sections.push('## Results');
  sections.push('');

  for (const result of successfulResults) {
    const seconds = Math.round(result.responseTime / 1000);

    sections.push(`### ${result.modelName} (${seconds}s)`);
    sections.push('');

    // Result content
    if (result.result !== undefined) {
      sections.push('```json');
      sections.push(formatResultContent(result.result));
      sections.push('```');
    }

    sections.push('');
    sections.push('---');
    sections.push('');
  }

  // Footer
  sections.push(
    '*Report generated by Prompt Debugger - Model Comparison Tool*'
  );

  return sections.join('\n');
}

/**
 * Fill prompt template with parameter values
 */
function fillPromptTemplate(
  template: string,
  parameters: Record<string, unknown>
): string {
  let filledPrompt = template;

  // Replace each {parameter-name} with its value
  for (const [key, value] of Object.entries(parameters)) {
    const placeholder = `{${key}}`;
    const displayValue =
      value !== null && value !== undefined ? String(value) : '';
    filledPrompt = filledPrompt.replace(
      new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
      displayValue
    );
  }

  return filledPrompt;
}

/**
 * Format result content for markdown display
 */
function formatResultContent(result: unknown): string {
  if (typeof result === 'string') {
    return result;
  }

  if (typeof result === 'object' && result !== null) {
    return JSON.stringify(result, null, 2);
  }

  return String(result);
}

/**
 * Trigger browser download of markdown content
 * @param content - Markdown content to download
 * @param filename - Filename for the download
 */
export function downloadMarkdownReport(
  content: string,
  filename: string
): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
